import { RawFlight } from "v2/api/flights";
import { Airline, FlightSelectorFlight, ItineraryFlight } from "v2/modules/types/intake-funnel";
import { airlines } from "v2/settings/airline-groups";

export function extractAirlineFromFSFlight(flight: FlightSelectorFlight): Airline {
  return {
    code: flight.airlineCode,
    name: flight.airlineName,
    iata: flight.airlineCode,
    icao: flight.airlineCode,
    displayName: flight.airlineName,
    uniqueIdentifier: flight?.airlineIdentifier,
    countryCode: "",
    ec261: false,
  };
}

export const filterDuplicatedFlights = (flights: RawFlight[]): RawFlight[] => {
  return flights.reduce((acc: RawFlight[], current: RawFlight) => {
    const duplicate = acc.find(
      (flight) =>
        flight.flight_number === current.flight_number &&
        flight.airline_identifier === current.airline_identifier,
    );
    if (!duplicate) {
      acc.push(current);
    }
    return acc;
  }, []);
};

export const filterIncompleteFlights = (flights: RawFlight[]) =>
  flights.filter(
    (flight) =>
      flight.flight_number &&
      flight.airline_identifier &&
      flight.local_departure_at &&
      flight.airline_code &&
      flight.airline_name,
  );

export function getDefaultFlightIdentifier(flight: ItineraryFlight | undefined) {
  if (!flight) return null;
  const uniqueIdentifier = flight.airline?.uniqueIdentifier;
  const airlineCode = flight.airline?.code;
  const flightNumber = flight.flightNumber;

  if (uniqueIdentifier && airlineCode && flightNumber) {
    return uniqueIdentifier + airlineCode + flightNumber;
  }
  return null;
}

export const CANT_FIND_FLIGHT_ID = "cant-find";

export function isCantFindFlightChecked(flightIdentifier: string | null) {
  return flightIdentifier === CANT_FIND_FLIGHT_ID;
}
const prefixWithZero = (value: number) => (value < 10 ? `0${value}` : value);

export function formatDate(date: Date | undefined | null = new Date()) {
  if (!date) return undefined;
  return `${prefixWithZero(date.getHours())}:${prefixWithZero(date.getMinutes())}`;
}

export function sortFlightsByDepartureTime(flights: FlightSelectorFlight[]) {
  return flights.sort((flight1, flight2) => {
    if (flight1.localDepartureAt && flight2.localDepartureAt) {
      return flight1.localDepartureAt.getTime() - flight2.localDepartureAt.getTime();
    }
    return 0;
  });
}

/**
 * Checks if a given key exists as an own property on an object and narrows the type accordingly.
 */
function hasOwnProperty<T extends object, K extends PropertyKey>(
  obj: T,
  key: K,
): obj is T & Record<K, number> {
  return Object.prototype.hasOwnProperty.call(obj, key);
}

/**
 * Filters a list of flights based on the specified airline ID.
 * If the airline ID maps to a group, all flights belonging to that group are returned.
 * Otherwise, it returns flights that exactly match the airline ID.
 * Airline groups are defined in the airline-groups.ts.
 * e.g. Wizz Air Uk (W9) is part of the Wizz Air group (W6).
 * @param flights - Array of flights to filter.
 * @param airlineId - The airline ID used to filter the flights.
 * @returns Filtered array of flights matching the airline ID or airline group.
 */
export function filterByAirlineId(
  flights: FlightSelectorFlight[],
  airlineId: string,
): FlightSelectorFlight[] {
  const airlineGroupId = hasOwnProperty(airlines.map, airlineId)
    ? airlines.map[airlineId]
    : undefined;
  if (airlineGroupId !== undefined) {
    const allFlights = flights.filter((flight) => {
      return airlines.groups[airlineGroupId].includes(flight.airlineIdentifier);
    });
    return allFlights;
  } else {
    return flights.filter((flight) => flight.airlineIdentifier === airlineId);
  }
}
